import { useState, useEffect, useCallback } from "react";
import {
  useGetList,
  useDataProvider,
  useNotify,
  useRefresh,
} from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import {
  Building2,
  Save,
  Loader2,
  Settings,
  Users,
} from "lucide-react";
import { OrgMembersList } from "./OrgMembersList";
import { OrgPhoneNumbers } from "./OrgPhoneNumbers";
import { StripeConnectCard } from "../stripe/StripeConnectCard";

interface Organization {
  id: string;
  name: string;
  twilio_phone_number: string | null;
  settings: Record<string, unknown>;
  stripe_account_id: string | null;
  stripe_account_status: string;
  stripe_onboarded_at: string | null;
  platform_fee_pct: number;
  platform_fee_fixed: number;
  created_at: string;
}

/**
 * Organization settings page.
 * Registered as a CustomRoute in CRM.tsx.
 */
export function OrgSettingsPage() {
  const {
    data: orgs,
    isPending,
    error,
  } = useGetList<Organization>("organizations", {
    pagination: { page: 1, perPage: 1 },
  });

  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const [orgName, setOrgName] = useState("");
  const [saving, setSaving] = useState(false);

  const org = orgs?.[0];

  useEffect(() => {
    if (org) {
      setOrgName(org.name || "");
    }
  }, [org]);

  const handleSave = useCallback(async () => {
    if (!org) return;
    setSaving(true);
    try {
      await dataProvider.update("organizations", {
        id: org.id,
        data: {
          name: orgName.trim(),
        },
        previousData: org,
      });
      notify("Settings saved", { type: "success" });
      refresh();
    } catch (err) {
      notify(`Save failed: ${(err as Error).message}`, { type: "error" });
    } finally {
      setSaving(false);
    }
  }, [org, orgName, dataProvider, notify, refresh]);

  if (isPending) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        Loading organization…
      </div>
    );
  }

  if (error || !org) {
    return (
      <div className="p-8">
        <Card>
          <CardContent className="p-6 text-center">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-semibold mb-2">
              No organization found
            </h3>
            <p className="text-sm text-muted-foreground">
              Return to the dashboard to set up your organization.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4 md:p-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Settings className="w-6 h-6" />
          Organization Settings
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage your BldrOS organization.
        </p>
      </div>

      {/* Org details */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Details
          </h2>

          <label className="block">
            <span className="text-sm font-medium mb-1 block">
              Company name
            </span>
            <input
              type="text"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </label>

          <button
            onClick={handleSave}
            disabled={saving || !orgName.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {saving ? "Saving…" : "Save changes"}
          </button>
        </CardContent>
      </Card>

      {/* Phone Numbers */}
      {org && (
        <Card>
          <CardContent className="p-6">
            <OrgPhoneNumbers orgId={org.id} />
          </CardContent>
        </Card>
      )}

      {/* Stripe */}
      {org && (
        <Card>
          <CardContent className="p-6">
            <StripeConnectCard
              orgId={org.id}
              stripeAccountId={org.stripe_account_id}
              stripeAccountStatus={org.stripe_account_status}
              stripeOnboardedAt={org.stripe_onboarded_at}
              platformFeePct={Number(org.platform_fee_pct)}
              platformFeeFixed={Number(org.platform_fee_fixed)}
            />
          </CardContent>
        </Card>
      )}

      {/* Members */}
      <Card>
        <CardContent className="p-6">
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
            <Users className="w-5 h-5" />
            Team members
          </h2>

          <OrgMembersList />
        </CardContent>
      </Card>
    </div>
  );
}

/** Path constant for route registration */
OrgSettingsPage.path = "/org-settings";
